/* eslint-disable react-hooks/exhaustive-deps */
import { DownOutlined, LoadingOutlined } from "@ant-design/icons";
import { useNotification } from "@refinedev/core";
import {
  Checkbox,
  Form,
  FormProps,
  Input,
  Modal,
  ModalProps,
  Spin,
  Typography,
} from "antd";
import isEqual from "lodash.isequal";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  useGetDetailCompany,
  useGetInfoByUserCode,
  useUpdateCompanyUser,
} from "../../../api/company";
import { ICompany } from "../../../interfaces/companies";
import { ButtonListFooterWithDeleteModalSM } from "../../../pages/components/buttonsList";
import {
  confirmModalDeleteProps,
  confirmModalUnsavedChangesProps,
} from "../../../pages/components/constants";
import { intermediaryBankTelRules as companyTelRulesSimple } from "../../../pages/payment-request/addBank.constants";
import {
  companyAddress1Rules,
  companyAddress2Rules,
  companyCityRules,
  companyCountryRules,
  companyDirectorsFirstNameRules,
  companyDirectorsLastNameRules,
  companyEmailRules,
  companyPostalCodeRules,
  companyRegistrationNumberRules,
  companyStateProvinceRules,
  companyTinVatUidRules,
  ProviderFullNameRules,
  ProviderNameRules
} from "../../../pages/payment-request/addCompany.constants";
import { fetchFurtherInformation } from "../../../utils/fetchFurtherInformation";
import {
  clearFieldErrorMessageOnChange,
  websiteRules,
} from "../../../utils/form.constants";
import { useDeleteCompany } from "../../../utils/hooks/useDeleteCompany.hook";
import { useNotificationOkCancel } from "../../../utils/hooks/useNotificationOkCancel.hook";
import { VerificationStatus } from "../../../utils/hooks/useWorldCheckVerification.hook";
import { renderWorldcheckVerificationStatus } from "../../../utils/renderWorldCheckVerificationStatus";
import { AutoComplete, SmartUidAutoCompleteInput } from "../../inputs/autocomplete";

interface IProps {
  formProps: FormProps;
  modalProps?: ModalProps;
  closeModal: () => void;
  editingId?: number;
  changeEditingId: (id: number | undefined) => void;
  refetchList: () => void;
  show: () => void;
}

export const EditCompany = ({
  modalProps,
  formProps,
  editingId,
  changeEditingId,
  closeModal,
  refetchList,
  show,
}: IProps) => {
  const { open: openNotificationResponse } = useNotification();

  const { data: loadData, isLoading: loadIsLoading } = useGetDetailCompany(
    editingId ? Number(editingId) : 0
  );

  const { mutate: updateComapny, isLoading: loadingUpdate } =
    useUpdateCompanyUser({
      onSuccess: (response: { data: { message: string } }) => {
        if (openNotificationResponse) {
          openNotificationResponse({
            key: "success_transaction_confirm",
            message: response.data.message || "Update Company successfully!",
            type: "success",
          });
        }
        refetchList();
        onUnsavedChangesOk();
        closeModal();
      },
      onError: (error: { message: string }) => {
        if (openNotificationResponse) {
          openNotificationResponse({
            key: "update_failed",
            message: error.message || "Update Company failed!",
            type: "error",
          });
        }
      },
    });

  const [loadedFormValues, setLoadedFormValues] = useState({});
  const [fetchingDetails, setFetchingDetails] = useState<boolean>(false);
  const [infoByUserCode, setInfoByUserCode] = useState<any>(null);
  const [disableField, setDisableField] = useState(false);

  const { mutate: fetchInfoByUserCode } = useGetInfoByUserCode({
    onSuccess: (response: { data: { data: any } }) => {
      setInfoByUserCode(response?.data?.data);
      formProps.form?.setFields([
        {
          name: "smartUID",
          errors: undefined,
        },
      ]);
    },
    onError: (error: { message: string; reason_code: string }) => {
      setInfoByUserCode(
        error?.reason_code === "ERR00010"
          ? "This SMART UID does not exist in SMART+"
          : error?.message
      );
      if (error?.reason_code === "ERR00010") {
        formProps.form?.setFields([
          {
            name: "smartUID",
            errors: ["This SMART UID does not exist in SMART+"],
          },
        ]);
      } else {
        formProps.form?.setFields([
          {
            name: "smartUID",
            errors: [error?.message || ""],
          },
        ]);
      }
    },
  });

  const { deleteCompany } = useDeleteCompany();

  const onOkDelete = useCallback(async () => {
    if (typeof editingId === "number") {
      changeEditingId(undefined);
      await deleteCompany(editingId);
      closeModal();
      refetchList();
    }
  }, [changeEditingId, closeModal, deleteCompany, editingId, refetchList]);

  const { openNotification, contextHolder: deleteContextHolder } =
    useNotificationOkCancel({
      ...confirmModalDeleteProps,
      onOk: onOkDelete,
    });

  const formIsLoading = loadIsLoading || loadingUpdate;

  const handleSmartUidLookup = useCallback(async (selectedCode?: string) => {
    const code = (selectedCode ?? formProps.form?.getFieldValue("smartUID") ?? "").trim();

    if (!code) {
      setDisableField(false);
      formProps.form?.setFields([
        {
          name: "smartUID",
          errors: ["This field is required"],
        },
      ]);
      setInfoByUserCode(null);
      return;
    }
    await fetchInfoByUserCode(code);
  }, [fetchInfoByUserCode, formProps.form]);

  useEffect(() => {
    if (
      formProps?.form?.getFieldValue("smartUID")?.trim() ===
      loadData?.data?.refUser?.smartUID
    ) {
      setDisableField(true);
      formProps?.form?.setFieldsValue({
        companyName:
          loadData?.data?.refUser?.fullName || loadData?.data?.fullName || "",
        address1:
          loadData?.data?.refUser?.address || loadData?.data?.address1 || "",
        country:
          loadData?.data?.refUser?.country || loadData?.data?.country || "",
        regNo: loadData?.data?.refUser?.regNo || loadData?.data?.regNo || "",
        email: loadData?.data?.refUser?.email || loadData?.data?.email || "",
        website:
          loadData?.data?.refUser?.website || loadData?.data?.website || "",
      });
      return;
    }
    if (!infoByUserCode || typeof infoByUserCode === "string") {
      formProps?.form?.resetFields([
        "companyName",
        "address1",
        "country",
        "regNo",
        "email",
        "website",
      ]);
      setDisableField(false);
      return;
    }
    setDisableField(true);
    formProps.form?.setFieldsValue({
      fullName: infoByUserCode?.fullName,
      address1: infoByUserCode?.address,
      country: infoByUserCode?.country,
      regNo: infoByUserCode?.regNo,
      email: infoByUserCode?.email,
      website: infoByUserCode?.website,
    });
  }, [formProps.form, infoByUserCode]);

  useEffect(() => {
    if (loadData) {
      formProps?.form?.setFieldsValue({
        name: loadData?.data.name,
        fullName: loadData?.data?.refUser?.fullName
          ? loadData?.data?.refUser?.fullName
          : loadData?.data.fullName,
        address1: loadData?.data?.refUser?.address
          ? loadData?.data?.refUser?.address
          : loadData?.data.address1,
        address2: loadData?.data.address2,
        directorFirstName: loadData?.data.directorFirstName,
        directorLastName: loadData?.data.directorLastName,
        country: loadData?.data?.refUser?.country
          ? loadData?.data?.refUser?.country
          : loadData?.data.country,
        postalCode: loadData?.data.postalCode,
        stateProvince: loadData?.data.stateProvince,
        city: loadData?.data.city,
        tinVatUid: loadData?.data.tinVatUid,
        regNo: loadData?.data?.refUser?.regNo
          ? loadData?.data?.refUser?.regNo
          : loadData?.data.regNo,
        phoneNumber1: loadData?.data.phoneNumber1,
        phoneNumber2: loadData?.data.phoneNumber2,
        email: loadData?.data?.refUser?.email
          ? loadData?.data?.refUser?.email
          : loadData?.data.email,
        website: loadData?.data?.refUser?.website
          ? loadData?.data?.refUser?.website
          : loadData?.data.website,
        smartUID: loadData?.data?.refUser?.smartUID || undefined,
        default: loadData?.data?.default,
      });
      setLoadedFormValues(formProps?.form?.getFieldsValue());
      if (loadData?.data?.refUser?.smartUID) {
        fetchInfoByUserCode(loadData?.data?.refUser?.smartUID);
      }
    } else {
      closeModal();
    }
  }, [loadData, formProps?.form, closeModal]);

  const hideDeleteBtn = useMemo(() => {
    return !loadData?.data?.canBeDeleted;
  }, [loadData]);

  useEffect(() => {
    if (editingId !== undefined) {
      show();
    } else {
      closeModal();
    }
  }, [editingId, closeModal, show]);

  const onUnsavedChangesOk = useCallback(() => {
    formProps?.form?.resetFields();
    changeEditingId(undefined);
  }, [formProps.form, changeEditingId]);

  const onSubmit = useCallback(
    async (formValues: ICompany) => {
      await updateComapny({
        id: Number(editingId),
        name: formValues.name,
        fullName: formValues.fullName?.length ? formValues.fullName : null,
        address1: formValues.address1,
        address2: formValues.address2?.length ? formValues.address2 : null,
        directorFirstName: formValues.directorFirstName,
        directorLastName: formValues.directorLastName,
        country: formValues.country,
        stateProvince: formValues.stateProvince,
        postalCode: formValues.postalCode,
        city: formValues.city,
        tinVatUid: formValues.tinVatUid?.length ? formValues.tinVatUid : null,
        regNo: formValues.regNo?.length ? formValues.regNo : null,
        phoneNumber1: formValues.phoneNumber1?.length
          ? formValues.phoneNumber1
          : null,
        phoneNumber2: formValues.phoneNumber2?.length
          ? formValues.phoneNumber2
          : null,
        email: formValues.email?.length ? formValues.email : null,
        website: formValues.website?.length ? formValues.website : null,
        refUserId: infoByUserCode?.id ? infoByUserCode?.id : undefined,
        default: formValues.default,
      });
    },
    [refetchList, updateComapny, closeModal, onUnsavedChangesOk, infoByUserCode]
  );

  const {
    openNotification: openUnsavedChangesNotification,
    contextHolder: unsavedChangesContextHolder,
  } = useNotificationOkCancel({
    ...confirmModalUnsavedChangesProps,
    onOk: onUnsavedChangesOk,
  });

  const onModalCancel = useCallback(() => {
    const isFormChanged = formProps
      ? !isEqual(formProps.form?.getFieldsValue(), loadedFormValues)
      : false;
    if (isFormChanged) {
      openUnsavedChangesNotification();
    } else {
      onUnsavedChangesOk();
    }
  }, [
    formProps,
    loadedFormValues,
    openUnsavedChangesNotification,
    onUnsavedChangesOk,
  ]);

  const onSave = useCallback(() => {
    formProps.form?.submit();
  }, [formProps]);

  const Title = () => (
    <>
      <p style={{ marginBottom: "0px" }}>Edit company information</p>
      <p>#{editingId}</p>
    </>
  );

  async function handleFetchFurtherInformation() {
    await fetchFurtherInformation(
      loadData?.data?.worldCheckVerification?.companyCaseId,
      loadData?.data?.worldCheckVerification?.individualCaseId,
      setFetchingDetails
    );
  }

  if (editingId === undefined) {
    return null;
  }

  return (
    <>
      <Modal
        {...modalProps}
        title={<Title />}
        className="smartModalTitle"
        width={""}
        onCancel={onModalCancel}
        footer={ButtonListFooterWithDeleteModalSM(
          onSave,
          onModalCancel,
          openNotification,
          formIsLoading,
          hideDeleteBtn
        )}
      >
        {loadData?.data?.worldCheckVerification && (
          <div style={{ display: "flex", justifyContent: "center" }}>
            {renderWorldcheckVerificationStatus(
              loadData?.data?.worldCheckVerification?.status
            )}
            {[
              VerificationStatus.HIGH_RISK_FIR,
              VerificationStatus.HIGH_RISK_EDD,
            ].includes(loadData?.data?.worldCheckVerification?.status) && (
                <Typography style={{ marginLeft: 8 }}>
                  <a onClick={handleFetchFurtherInformation}>See why</a>
                  {fetchingDetails && (
                    <Spin
                      indicator={
                        <LoadingOutlined
                          style={{
                            fontSize: 16,
                            color: "blue",
                            marginLeft: "8px",
                          }}
                          spin
                        />
                      }
                    />
                  )}
                </Typography>
              )}
          </div>
        )}
        {unsavedChangesContextHolder}
        {deleteContextHolder}
        <Form
          {...formProps}
          layout="vertical"
          onFinish={onSubmit}
          onValuesChange={clearFieldErrorMessageOnChange(formProps?.form)}
        >
          {loadData?.data?.refUser?.smartUID ? (
            <Form.Item
              name="smartUID"
              validateTrigger="onBlur"
              rules={[{
                required: true,
                message: "This field is required",
                whitespace: true,
              }]}
            >
              <SmartUidAutoCompleteInput
                onBlur={() => void handleSmartUidLookup()}
                onSuggestionSelect={(value) => void handleSmartUidLookup(value)}
                placeholder="SMART UID (min. 3 characters)"
                type="text"
              />
            </Form.Item>
          ) : null}
          <Form.Item name="name" rules={ProviderNameRules}>
            <Input placeholder="Name" type="text" />
          </Form.Item>
          <Form.Item name="fullName" rules={ProviderFullNameRules}>
            <Input
              disabled={disableField}
              placeholder="Full Name"
              type="text"
            />
          </Form.Item>
          <Form.Item
            name="directorFirstName"
            rules={companyDirectorsFirstNameRules}
          >
            <Input placeholder={`Director's first name`} />
          </Form.Item>
          <Form.Item
            name="directorLastName"
            rules={companyDirectorsLastNameRules}
          >
            <Input placeholder={`Director's last name`} />
          </Form.Item>
          <Form.Item name="address1" rules={companyAddress1Rules}>
            <Input
              disabled={disableField}
              placeholder="Address 1"
              type="text"
            />
          </Form.Item>
          <Form.Item name="address2" rules={companyAddress2Rules}>
            <Input placeholder="Address 2" type="text" />
          </Form.Item>
          <Form.Item name="city" rules={companyCityRules}>
            <Input placeholder={`City`} />
          </Form.Item>
          <Form.Item name="stateProvince" rules={companyStateProvinceRules}>
            <Input placeholder={`State / Province`} />
          </Form.Item>
          <Form.Item name="postalCode" rules={companyPostalCodeRules}>
            <Input placeholder={`Postal Code`} />
          </Form.Item>
          <Form.Item name="country" rules={companyCountryRules}>
            <AutoComplete
              disabled={disableField}
              resource="api/v1/worldCheck/countries"
              mapItem={(item: { value: string; label: string }) => ({
                value: item.label,
                key: item.value,
                label: <Typography.Text>{item.label}</Typography.Text>,
                data: item,
              })}
              notFoundPlaceholder="Start by typing country name"
            >
              <Input placeholder="Country of incorporation" suffix={<DownOutlined />} />
            </AutoComplete>
          </Form.Item>
          <Form.Item name="tinVatUid" rules={companyTinVatUidRules}>
            <Input placeholder="TIN" />
          </Form.Item>
          <Form.Item name="regNo" rules={companyRegistrationNumberRules}>
            <Input disabled={disableField} placeholder="Reg &#8470;" />
          </Form.Item>
          <Form.Item name="phoneNumber1" rules={companyTelRulesSimple}>
            <Input placeholder="Tel 1" />
          </Form.Item>
          <Form.Item name="phoneNumber2" rules={companyTelRulesSimple}>
            <Input placeholder="Tel 2" />
          </Form.Item>
          <Form.Item name="email" rules={companyEmailRules}>
            <Input disabled={disableField} placeholder="Email" />
          </Form.Item>
          <Form.Item name="website" rules={websiteRules}>
            <Input disabled={disableField} placeholder="Website" />
          </Form.Item>
          <Form.Item name="default" valuePropName="checked">
            <Checkbox>Default company</Checkbox>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};
